<template>
  <figure ref="root" class="sec-term" role="img" :aria-label="label">
    <div class="sec-term__bar" aria-hidden="true">
      <span class="sec-term__dots"><i></i><i></i><i></i></span>
      <span class="sec-mono sec-term__title">{{ title }}</span>
      <span class="sec-mono sec-term__status" :class="{ 'sec-term__status--running': running }">
        {{ running ? status.running : status.done }}
      </span>
    </div>

    <div class="sec-mono sec-term__body" aria-hidden="true">
      <p v-for="(line, i) in lines" :key="i" class="sec-term__line" :class="`sec-term__line--${line.kind}`">
        <span v-if="line.kind === 'cmd'" class="sec-term__prompt">$</span>
        <span v-else-if="line.kind === 'ok'" class="sec-term__mark">&#10003;</span>
        <span v-else-if="line.kind === 'fail'" class="sec-term__mark">&#10005;</span>
        <span class="sec-term__text">{{ line.text }}</span>
        <span v-if="line.kind === 'cmd'" class="sec-term__caret"></span>
      </p>
    </div>

    <div class="sec-mono sec-term__foot" aria-hidden="true">
      <span>{{ summary.label }}</span>
      <span class="sec-term__score"><b class="sec-term__count">{{ summary.to }}</b> / {{ summary.of }}</span>
    </div>
  </figure>
</template>

<script setup>
// Terminal-window illustration (Home hero). It plays a short session line by line: commands
// type out, findings and fixes fade in with their cross or tick, the badge switches from
// "running" to "done" and the footer counter counts up. The session loops while it is on
// screen and pauses off screen. Without motion (prefers-reduced-motion) it renders the
// finished session. Screen readers get `label` instead of the decorative lines.
import { ref } from 'vue';
import { gsap } from '@/plugins/gsap';
import { useMotion } from '@/composables/security/useMotion';

const props = defineProps({
  label: { type: String, required: true },
  title: { type: String, required: true },
  status: { type: Object, required: true }, // { running, done }
  lines: { type: Array, required: true }, // [{ kind: 'cmd' | 'note' | 'ok' | 'fail', text }]
  summary: { type: Object, required: true }, // { label, from, to, of }
});

const root = ref(null);
const running = ref(false);

useMotion(root, (el) => {
  const lineEls = gsap.utils.toArray('.sec-term__line', el);
  const count = el.querySelector('.sec-term__count');
  const counter = { n: props.summary.from };

  const reset = () => {
    running.value = true;
    counter.n = props.summary.from;
    count.textContent = props.summary.from;
  };

  const tl = gsap.timeline({
    repeat: -1,
    repeatDelay: 1,
    onStart: reset,
    onRepeat: reset,
    scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', toggleActions: 'play pause resume pause' },
  });

  tl.set(lineEls, { autoAlpha: 0 }).set(el.querySelectorAll('.sec-term__caret'), { autoAlpha: 0 });

  lineEls.forEach((lineEl, i) => {
    const line = props.lines[i];
    if (line.kind === 'cmd') {
      const text = lineEl.querySelector('.sec-term__text');
      const caret = lineEl.querySelector('.sec-term__caret');
      const typed = { n: 0 };
      tl.call(() => {
        text.textContent = '';
      })
        .set(lineEl, { autoAlpha: 1, y: 0 }, '+=0.3')
        .set(caret, { autoAlpha: 1 })
        .to(typed, {
          n: line.text.length,
          duration: Math.min(line.text.length * 0.03, 1.6),
          ease: 'none',
          onStart: () => {
            typed.n = 0;
          },
          onUpdate: () => {
            text.textContent = line.text.slice(0, Math.round(typed.n));
          },
        })
        .to({}, { duration: 0.35 })
        .set(caret, { autoAlpha: 0 });
    } else {
      tl.fromTo(lineEl, { autoAlpha: 0, y: 6 }, { autoAlpha: 1, y: 0, duration: 0.28, ease: 'power2.out' }, '+=0.22');
      const mark = lineEl.querySelector('.sec-term__mark');
      if (mark) tl.fromTo(mark, { scale: 0.3 }, { scale: 1, duration: 0.35, ease: 'back.out(3)' }, '<');
    }
  });

  tl.to(
    counter,
    {
      n: props.summary.to,
      duration: 1.1,
      ease: 'power1.out',
      onUpdate: () => {
        count.textContent = Math.round(counter.n);
      },
    },
    '-=1.2'
  )
    .call(() => {
      running.value = false;
    })
    .to({}, { duration: 3.5 })
    .to(lineEls, { autoAlpha: 0, duration: 0.4, stagger: 0.02 });

  // On revert (unmount, or reduced motion switched on) show the finished session again.
  return () => {
    running.value = false;
    count.textContent = props.summary.to;
    lineEls.forEach((lineEl, i) => {
      const text = lineEl.querySelector('.sec-term__text');
      if (text) text.textContent = props.lines[i].text;
    });
  };
});
</script>

<style scoped>
.sec-term {
  margin: 0;
  width: 100%;
  overflow: hidden;
  border-radius: 14px;
  background: var(--sec-term-bg);
  box-shadow: var(--sec-term-shadow);
  text-align: left;
}

.sec-term__bar {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--sec-s2);
  padding: 12px 16px;
  background: var(--sec-term-bar);
}

.sec-term__dots {
  display: flex;
  gap: 8px;
}

.sec-term__dots i {
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--sec-term-dot-1);
}

.sec-term__dots i:nth-child(2) {
  background: var(--sec-term-dot-2);
}

.sec-term__dots i:nth-child(3) {
  background: var(--sec-term-dot-3);
}

.sec-term__title {
  overflow: hidden;
  font-size: 0.75rem;
  color: var(--sec-term-muted);
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.sec-term__status {
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
  background: var(--sec-accent);
  color: var(--sec-on-bright);
  transition: background-color 250ms ease;
}

.sec-term__status--running {
  background: var(--sec-term-fail);
  /* Dark ink on Lotus Red: about 6:1 (white text was 2.9:1, below WCAG AA). */
  color: var(--sec-ink);
}

.sec-term__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 20px 20px 14px;
  font-size: 0.75rem;
  line-height: 1.75;
  color: var(--sec-term-text);
}

.sec-term__line {
  display: flex;
  align-items: baseline;
  gap: 0.6em;
  margin: 0;
}

.sec-term__line--cmd {
  margin-top: 8px;
}

.sec-term__line--cmd:first-child {
  margin-top: 0;
}

.sec-term__line--note {
  color: var(--sec-term-muted);
}

.sec-term__prompt {
  color: var(--sec-term-ok);
}

.sec-term__mark {
  display: inline-block;
  width: 1em;
  flex-shrink: 0;
  font-weight: 700;
}

.sec-term__line--ok .sec-term__mark {
  color: var(--sec-term-ok);
}

.sec-term__line--fail .sec-term__mark {
  color: var(--sec-term-fail);
}

.sec-term__text {
  min-width: 0;
  overflow-wrap: anywhere;
}

.sec-term__caret {
  flex-shrink: 0;
  width: 0.55em;
  height: 1.1em;
  align-self: center;
  margin-left: -0.45em;
  background: var(--sec-term-text);
  animation: sec-term-blink 1s steps(1) infinite;
}

.sec-term__foot {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sec-s2);
  margin: 0 20px;
  padding: 14px 0 18px;
  border-top: 1px solid var(--sec-term-rule);
  font-size: 0.75rem;
  color: var(--sec-term-muted);
}

.sec-term__count {
  font-size: 1rem;
  color: var(--sec-term-text);
  font-variant-numeric: tabular-nums;
}

@keyframes sec-term-blink {
  50% {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sec-term__caret {
    display: none;
  }
}

@media only screen and (min-width: 700px) {
  .sec-term__body {
    padding: 24px 28px 18px;
    font-size: 0.875rem;
  }
  .sec-term__foot {
    margin-inline: 28px;
    font-size: 0.8125rem;
  }
}
</style>
